import React, { useContext, useState } from "react";

import { IDispatchClient, Dashboard, Delivery, DeliveryGroup, DeliveryTracking } from "./model";
import { DeliveryStatus } from "../delivery_status";
import { DeliveryGroupSelect, DriverSelect } from "./selects";
import { useDashboardConfig } from "./dispatch_api_client";
import { classNames } from "src/common_util";
import { useDraggable } from "@dnd-kit/react";
import { CurrentTimeContext } from "./current_time_context";

export interface DeliveryComponentProps {
  defaultExpand: boolean;
  dashboard: Dashboard;
  delivery: Delivery;
  deliveryTracking?: DeliveryTracking;
  deliveringNow: boolean;
}

const DeliveryComponent = ({ defaultExpand, dashboard, delivery, deliveryTracking, deliveringNow } : DeliveryComponentProps) => {
  const { apiClient } = useDashboardConfig();
  const [expanded, setExpanded] = useState(false);

  const currentTime = useContext(CurrentTimeContext);

  const handleAllocate = async (driverId: number) => {
    if (driverId != -1) {
      await dashboard.handleAllocateDeliveryToDriver(delivery.id, driverId, apiClient);
    } else if (delivery.driverId) {
      await apiClient.deallocateDeliveryFromDriver(delivery.id, delivery.driverId)
    }
  }

  const handleGroup = async (groupId: number) => {
    if (groupId == -1 && delivery.groupId) {
      await apiClient.removeDeliveryFromGroup(delivery.id, delivery.groupId);
      return;
    }

    // if there is no user-selected group, attempt to quick-group
    if (groupId == -2) {
      // do not allow quick-group on allocated orders
      if (delivery.driverId != null) return;
      else {
        // find the first groupId that's not is use, or return -2 if all groups are in use
        const activeGroupIds = Array.from(dashboard.deliveriesByGroup()).map(n => n[0]);
        groupId = dashboard.groups.find(group => !activeGroupIds.includes(group.id))?.id || -2;

        if (groupId == -2) return;
      }
    }

    await apiClient.allocateDeliveryToGroup(delivery.id, groupId, delivery.driverId);
  }

  const toggleExpanded = () => {
    setExpanded(!expanded);
  }

  const filterDeliveryGroups = (groups: DeliveryGroup[], groupId: number | null) => {
    return groups.filter(function(ele){
      return ele.id != groupId
    });
  }

  const driverSelect = delivery.allowAllocation || delivery.allowDeallocation ?
    <DriverSelect
      drivers={dashboard.availableDriversForDeliveries([delivery])}
      allowUnallocate={delivery.allowDeallocation}
      onConfirm={handleAllocate}
    /> : <></>;

  const groupSelect = delivery.allowGrouping ?
    <DeliveryGroupSelect
      groups={filterDeliveryGroups(dashboard.groups, delivery.groupId)}
      allowUngroup={delivery.groupId != null}
      isUnallocatedOrder={delivery.driverId == null}
      onConfirm={handleGroup}
    /> : <></>;

  const makeNowFeature = delivery.makeNowFeature ? <span className="icon is-small"><i className="fas fa-exclamation"></i></span> : <></>;
  const showAlcohol = delivery.requiresAgeVerification ? <span className="icon is-small" title="Age restricted"><i className="fas fa-wine-glass"></i></span> : <></>;
  const showBusiness = delivery.deliveryBusinessName ? <span className="icon is-small" title="Business address"><i className="fas fa-building"></i></span> : <></>;
  const showDelaySent = delivery.delaySent ? <div className="ml-1"><span className="icon is-small" title="Late notification sent"><i className="fas fa-envelope"></i></span></div> : <></>;

  const expandedDeliverySelects = expanded || (defaultExpand && (delivery.allowAllocation || delivery.allowDeallocation || delivery.allowGrouping)) ?
    <div className="p-2">
      <div className="columns is-variable is-1">
        <div className="column is-half pr-2">
          <div className="mb-1 has-text-weight-medium">Assign</div>
          {driverSelect}
        </div>

        <div className="column is-half pl-2">
          <div className="mb-1 has-text-weight-medium">Group</div>
          {groupSelect}
        </div>
      </div>
    </div> : <></>;

  const expando = expanded
    ? <span className="icon is-small ml-1 order-expando"><i className="fas fa-xs fa-chevron-up"></i></span>
    : <span className="icon is-small ml-1 order-expando"><i className="fas fa-xs fa-chevron-down"></i></span>;

  const expandoActiveClass = expanded ? "is-active " : "";

  const updateTimeAgo = (lastUpdate?: Date) => {
    if (!lastUpdate) return undefined;

    const elapsedSeconds = (currentTime.getTime() - lastUpdate.getTime())/1000;

    if (elapsedSeconds < 20) {
      return '(updated just now)';
    } else if (elapsedSeconds < 60) {
      return '(updated in the last min)';
    } else if (elapsedSeconds < 120) {
      return '(updated 1 min ago)';
    } else {
      return `(updated ${Math.round(elapsedSeconds / 60)} mins ago)`;
    }
  }

  // eventually this time will replace the Do time in the order header here, but for now we just pass it down to the extended info component
  const liveDropoffTime = deliveryTracking?.liveArrivalEstimate?.toLocaleString("en", { timeStyle: "short" }).replace(' ', '').toLowerCase();
  const liveUpdateTimeAgo = updateTimeAgo(deliveryTracking?.liveLastUpdate);

  const readyForPickupTitle = delivery.readyToCollect ? "Ready for pickup" : "Pickup time";

  const pickupTime = <>
    <div>
      <div title={readyForPickupTitle} className="pickup-time">
        <span className="has-text-grey">
          <span className="icon is-small">
            <i className="far fa-sm fa-clock"></i>
          </span>
          <span>Pu</span>
        </span>
        &nbsp;<strong>{delivery.pickedUpAt?.toUpperCase() || delivery.pickupTime.toUpperCase()}</strong>
      </div>
    </div>
  </>;

  const dropoffTime = delivery.pickedUpAt !== null ?
    <div>
      <div title="Drop off time" className="dropoff-time">
        <span className="has-text-grey">
          <span className="icon is-small">
            <i className="far fa-sm fa-clock"></i>
          </span>
          <span>Do</span>
        </span>
        &nbsp;<strong>{delivery.deliveryTime.toUpperCase()}</strong>
      </div>
    </div>
    : <></>;

  const tenMinutesOverdue = () => {
    return pickupOverdueBy(10) || deliveryOverdueBy(10);
  }

  const fifteenMinutesOverdue = () => {
    return pickupOverdueBy(15) || deliveryOverdueBy(15);
  }

  const thirtyMinutesOverdue = () => {
    return pickupOverdueBy(30) || deliveryOverdueBy(30);
  }

  const pickupOverdueBy = (minutes: number) => {
    return !delivery.pickedUpAt && currentTime.getTime() - delivery.pickupTimeIso8601.getTime() > minutes*60*1000;
  }

  const deliveryOverdueBy = (minutes: number) => {
    return currentTime.getTime() - delivery.dropoffTimeIso8601.getTime() > minutes*60*1000;
  }

  const operationsClasses : string[] = [
    delivery.driverReviewing() ? "not-accepted-by-driver" : "",
    delivery.deliveryStatus == DeliveryStatus.HoldingForStoreResponse ? "unacknowledged-by-restaurant" : "",
    delivery.readyToCollect ? "ready-for-pickup" : "",
    tenMinutesOverdue() ? "ten-minutes-overdue" : "",
    fifteenMinutesOverdue() ? "fifteen-minutes-overdue" : "",
    thirtyMinutesOverdue() ? "thirty-minutes-overdue" : "",
    delivery.operationsException ? "operations-exception" : "",
  ];

  const primaryOrderTitle : string = (delivery.operationsException && "Operations exception, action required") ||
    (delivery.makeNowFeature && "Using make now feature") ||
    (delivery.deliveryStatus == DeliveryStatus.HoldingForStoreResponse && "Store hasn't acknowledged order") ||
    (thirtyMinutesOverdue() && "Thirty minutes overdue") ||
    (fifteenMinutesOverdue() && "Fifteen minutes overdue") ||
    (tenMinutesOverdue() && "Ten minutes overdue") || ""

  const acknowledgementTitle = delivery.driverReviewing() ? "Driver hasn't accepted order" : "";

  const extendedInfo = expanded ?
    <DeliveryExtendedInfo
      delivery={delivery}
      deliveringNow={deliveringNow}
      liveDropoffTime={liveDropoffTime}
      liveUpdateTimeAgo={liveUpdateTimeAgo}
      apiClient={apiClient}
    /> : <></>;

  const notifications = delivery.notifications.length > 0 ?
    <div className="order-notification has-text-grey">
      <span className="icon is-small pr-1">
        <i className="far fa-sm fa-sticky-note"></i>
      </span>

      {delivery.notifications.join(' · ')}

      {showDelaySent}
    </div>
    : <></>;

  return (
    <>
      <div className={"order " + expandoActiveClass + operationsClasses.join(" ")}>
        <div title={primaryOrderTitle} className="order-header" onClick={toggleExpanded}>
          <div title={acknowledgementTitle} className="driver-reviewing-indicator"></div>

          <div className="pickup-from">
            <div className="is-flex-grow-1">
              {makeNowFeature}
              {showAlcohol}
              {delivery.storeName}
            </div>

            {pickupTime}

            {expando}
          </div>

          <div className="deliver-to">
            <div className="is-flex-grow-1">
              <span className="icon is-small pr-1">
                <i className="fas fa-xs fa-arrow-right"></i>
              </span>
              <span>
                {showBusiness}
                <b>{delivery.suburb}</b>
              </span>
            </div>

            {dropoffTime}

            <MaplinkNode deliveries={[ delivery ]} />
          </div>

          {notifications}

          {delivery.dashNote ?
            <div>
              <span className="dash-note px-1 is-rounded-small has-text-weight-bold overflow-anywhere">{delivery.dashNote}</span>
            </div>
            : <></>}
        </div>

        {expandedDeliverySelects}
        {extendedInfo}
      </div>
    </>
  );
}

const ItemComponent = ({ description }: {
  description: string;
}) => {
  const [title, ...details] = description.split("\n");
  // eslint-disable-next-line react/jsx-key
  const detailNodes = details.map((desc) => <div>{desc}</div>);

  return (
    <div className="py-1">
      <div>
        <b>{title}</b>
      </div>

      <div className="is-hidden-touch">
        <div>
          {detailNodes}
        </div>
      </div>
    </div>
  );
}

interface DeliveryExtendedInfoProps {
  delivery: Delivery;
  deliveringNow: boolean;
  liveDropoffTime?: string;
  liveUpdateTimeAgo?: string;
  apiClient: IDispatchClient;
}

const DeliveryExtendedInfo = ({ delivery, deliveringNow, liveDropoffTime, liveUpdateTimeAgo, apiClient } : DeliveryExtendedInfoProps) => {
  const [delayTime, setDelayTime] = useState('10');
  const [delayReason, setDelayReason] = useState('delivereasy');
  const [prepDelay, setPrepDelay] = useState('10');
  const [sending, setSending] = useState(false);
  const [prepDelaySending, setPrepDelaySending] = useState(false);
  const [dashNoteInput, setDashNoteInput] = useState(delivery.dashNote);


  const changeDelayTime = (ev: React.ChangeEvent<HTMLSelectElement>)=> {
    setDelayTime(ev.target.value);
  }

  const changeDelayReason = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    setDelayReason(ev.target.value);
  }

  const handleSendText = async () => {
    setSending(true);

    await apiClient.sendDelayMessage(delivery.id, delayTime, delayReason);
  }

  const changePrepDelay = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    setPrepDelay(ev.target.value);
  }

  const handleUpdatePrepDelay = async () => {
    setPrepDelaySending(true);

    await apiClient.delayPrep(delivery.id, prepDelay);
  }

  const handleDashNoteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setDashNoteInput(target.value);
  }

  const handleDashNoteKeypress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter')
      await updateDashNote();
  }

  const updateDashNote= async () => {
    if (delivery.dashNote != dashNoteInput)
      await apiClient.updateDashNote(delivery.id, dashNoteInput);
  }

  // oxlint-disable-next-line eslint-plugin-react(jsx-key)
  const items = delivery.packageDescription.map(description => {
    return (
      // eslint-disable-next-line react/jsx-key
      <ItemComponent description={description} />
    );
  });

  const deliveryMessage = delivery.allowDelayMessage ?
    <>
      <div className="p-2">
        <div className="mb-1 is-uppercase">
          Text customer
        </div>

        <div className="columns is-variable is-1">
          <div className="column is-half pr-2">
            <div className="mb-1 has-text-weight-medium">
              Delay
            </div>

            <div className="field has-addons">
              <div className="control is-expanded">
                <div className="select is-small is-fullwidth is-radiusless">
                  <select className="is-radiusless pl-1" value={delayTime} onChange={changeDelayTime}>
                    <option value='10'>10 minutes</option>
                    <option value='15'>15 minutes</option>
                    <option value='20'>20 minutes</option>
                    <option value='30'>30 minutes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="column is-half pl-2">
            <div className="mb-1 has-text-weight-medium">
              Reason
            </div>

            <div className="field has-addons">
              <div className="control is-expanded">
                <div className="select is-small is-fullwidth is-radiusless">
                  <select className="is-radiusless pl-1" value={delayReason} onChange={changeDelayReason}>
                    <option value='delivereasy'>Our Bad</option>
                    <option value='restaurant'>Restaurants Bad</option>
                    <option value='traffic'>Traffic</option>
                  </select>
                </div>
              </div>
              <div className="control">
                <button className={`button is-small ${sending ? "is-loading" : ""}`} disabled={sending} onClick={() => void handleSendText()}>
                    <span className="icon">
                      <i className="fas fa-caret-right"></i>
                    </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </> : (delivery.delaySent ? <>
      <div className="p-2"><div className="mb-1 is-uppercase">Text customer</div><div><strong>{delivery.delaySent + " minute delay sent"}</strong></div></div>
    </> : <></>);

  const delayPrep = delivery.allowPrepDelay ? <>
    <div className="px-2 pb-2">
      <div className="columns is-variable is-1">
        <div className="column is-half">
          <div className="mb-1 is-uppercase">
            Delay Prep
          </div>

          <div className="field has-addons">
            <div className="control is-expanded">
              <div className="select is-small is-fullwidth is-radiusless">
                <select className="is-radiusless pl-1" value={prepDelay} onChange={changePrepDelay}>
                  <option value='10'>Delay by 10 minutes</option>
                  <option value='20'>Delay by 20 minutes</option>
                </select>
              </div>
            </div>
            <div className="control">
              <button className={`button is-small ${prepDelaySending ? "is-loading" : ""}`} disabled={prepDelaySending} onClick={() => void handleUpdatePrepDelay()}>
                  <span className="icon">
                    <i className="fas fa-caret-right"></i>
                  </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </> : (delivery.prepDelay ? <><div className="px-2 pb-2"><div className="mb-1 is-uppercase">Delay Prep</div><div><strong>{"Order prep delayed by " + delivery.prepDelay + " minutes"}</strong></div></div></> : <></>);

  return (
    <>
      {deliveryMessage}

      <div className="px-2 pt-2">
        <a className="button is-fullwidth is-small mb-3" href={delivery.href} target="_blank" rel="noreferrer">
          <span>Open order</span>

          <span className="icon is-small">
              <i className="fas fa-external-link-alt"></i>
            </span>
        </a>

        <div className="mb-2 is-flex is-gap-1">
          <input className="is-flex-grow-1 dash-note-input" type="text" placeholder="Add dash note" defaultValue={delivery.dashNote} onChange={handleDashNoteInputChange} onKeyDown={(e) => void handleDashNoteKeypress(e)} />
          <button className="button is-small" onClick={() => void updateDashNote()}>Save</button>
        </div>

        <table className="table is-narrow is-fullwidth">
          <tbody>
          <tr>
            <th>#</th>
            <td>
                  <span data-controller="clipboard" data-clipboard-content={`#${delivery.orderId}`}>
                    <span data-clipboard-target="clipboard" data-tooltip="Copy to clipboard" data-action="click->clipboard#copyToClipboard mouseleave->clipboard#resetTooltip">
                      {delivery.orderId}
                      <span className="icon">
                        <i className="fas fa-clipboard"></i>
                      </span>
                    </span>
                  </span>
            </td>
            <td></td>
          </tr>

          <tr>
            <th>Rest ph</th>
            <td><a href={`aircall://${delivery.storePhone.replaceAll(/\s+/g, "")}`}>{delivery.storePhone}</a></td>
            <td>
              <a
                href={`https://maps.google.com/?q=${delivery.storeAddress}`}
                target="_blank"
                rel="noreferrer">
                map
              </a>
            </td>
          </tr>

          <tr>
            <th>Address</th>
            <td>{delivery.deliveryAddress}</td>
            <td>
              <a
                href={`https://maps.google.com/?q=${delivery.deliveryAddress}`}
                target="_blank"
                rel="noreferrer">
                map
              </a>
            </td>
          </tr>

          {delivery.deliveryBusinessName ?
            <tr>
              <th>Business</th>
              <td>{delivery.deliveryBusinessName}</td>
            </tr> : <></>}

          <tr>
            <th>Name</th>
            <td>{delivery.name}</td>
            <td></td>
          </tr>

          {delivery.bookingIdentifier ?
            <tr>
              <th>Booking</th>
              <td>{delivery.bookingIdentifier}</td>
              <td></td>
            </tr> : <></>}

          {delivery.docketReference ?
            <tr>
              <th>Docket</th>
              <td>{delivery.docketReference}</td>
              <td></td>
            </tr> : <></>}

          <tr>
            <th>Phone</th>
            <td><a href={`aircall://${delivery.phone.replaceAll(/\s+/g, "")}`}>{delivery.phone}</a></td>
            <td></td>
          </tr>

          {delivery.latestAssignmentTime ?
            <tr>
              <th>Allocated</th>
              <td>{delivery.latestAssignmentTime}</td>
              <td></td>
            </tr> : <></>}

          <tr>
            <th>Pickup time</th>
            <td>
              <span>{delivery.pickupTime} </span>
              {
                delivery.pickupTime != delivery.originalPickupTime ?
                  <span style={{ textDecoration: "line-through" }}>{delivery.originalPickupTime}</span> :
                  null
              }
            </td>
            <td></td>
          </tr>

          <tr>
            <th>Delivery time</th>
            <td>
              <span>{delivery.deliveryTime} </span>
              {
                delivery.deliveryTime != delivery.originalDeliveryTime ?
                  <span style={{ textDecoration: "line-through" }}>{delivery.originalDeliveryTime}</span> :
                  null
              }
            </td>
            <td></td>
          </tr>

          {deliveringNow ?
            <tr>
              <th>Live delivery time</th>
              <td colSpan={2}>{liveDropoffTime} {liveUpdateTimeAgo ?? <span><i className="fas fa-spinner fa-pulse"></i></span>}</td>
            </tr> : <></>}

          <tr>
            <th>Delivery inst.</th>
            <td>{delivery.deliveryInstructions}</td>
            <td></td>
          </tr>

          <tr>
            <td colSpan={3}>{items}</td>
          </tr>
          </tbody>
        </table>
      </div>

      {delayPrep}
    </>
  );
}

const MaplinkNode = ({ deliveries }: {
  deliveries: Delivery[];
}) => {
  // Use sets to remove duplicates, then convert back to an array for array functions
  const deliveryAddressesOrig: string[] = deliveries.map(delivery => delivery.deliveryAddress);
  const storeAddressesSet: Set<string> = new Set(deliveries.map(delivery => delivery.storeAddress));
  const deliveryAddressesSet: Set<string> = new Set(deliveryAddressesOrig);
  const storeAddresses: string[] = Array.from(storeAddressesSet);
  const deliveryAddresses: string[] = Array.from(deliveryAddressesSet);

  const sameAddressIndicator = deliveryAddressesOrig.length > deliveryAddresses.length ?
    <i className="fa fa-solid fa-star same-address-indicator" title="Multiple orders going to the same address"></i>
    : <></>;
  const origin: string = storeAddresses.shift() || "";
  const destination: string = deliveryAddresses.pop() || "";
  const waypoints: string = storeAddresses.concat(deliveryAddresses).join("|");

  const url: URL = new URL("/maps/dir/", "https://www.google.com");
  url.searchParams.set("origin", origin);
  url.searchParams.set("destination", destination);
  if (waypoints)
    url.searchParams.set("waypoints", waypoints);
  url.searchParams.set("travelmode", "driving");
  url.searchParams.set("api", "1");

  return (
    <a className="maplink" href={url.href} target="_blank" title="Open Google Map Directions" rel="noreferrer" onClick={e => e.stopPropagation()}>
      {sameAddressIndicator}
      <i className="fa fa-solid fa-map"></i>
    </a>
  )
};

const useDraggableDelivery = (delivery: Delivery) => {
  const [dropping, setDropping] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorTimerId, setErrorTimerId] = useState(0);

  const handleDropping = () => {
    setDropping(true);
  }

  const handleDragFailure = () => {
    setDropping(false);
    const errorTimer = window.setTimeout(() => setHasError(false), 1500);
    setErrorTimerId(errorTimer);
    setHasError(true);
  }

  const unsetErrorTimer = () => {
    window.clearTimeout(errorTimerId);
    setErrorTimerId(0);
    setHasError(false);
  }

  const { ref, isDragging } = useDraggable({
    id: `delivery:${delivery.id}`,
    data: {
      id: delivery.id,
      groupId: delivery.groupId,
      type: "delivery",
      handleDropping,
      handleDragFailure,
      unsetErrorTimer
    },
    // Only allow dragging on orders that are either make-now or acknowledged by the restaurant, & not currently being dropped
    disabled: (!delivery.makeNowFeature && !delivery.allowAllocation && !delivery.allowGrouping) || dropping,
  });

  const style = isDragging ? {
    opacity: 0.3
  } : undefined;

  const classes = classNames({
    "is-dragged-node": isDragging,
    "has-drag-error": hasError,
    "is-half-opacity": dropping
  });

  return {
    classes: classes,
    style: style,
    ref: ref,
  }
}

const IncomingDelivery = (props: DeliveryComponentProps): React.JSX.Element => {
  const { classes, style, ref } = useDraggableDelivery(props.delivery);

  return (
    <div className={`card is-card-dashboard ${classes}`} style={style} ref={ref}>
      <div className="card-content p-0">
        <DeliveryComponent
          {...props}
        />
      </div>
    </div>
  );
}

const GroupedDelivery = (props: DeliveryComponentProps): React.JSX.Element => {
  const { classes, style, ref } = useDraggableDelivery(props.delivery);

  return (
    <div className={classes} style={style} ref={ref}>
      <DeliveryComponent
        {...props}
      />
    </div>
  );
}

export { IncomingDelivery, GroupedDelivery, DeliveryComponent, MaplinkNode };
